import { api } from './api.js';

/**
 * Fotos in Formularen (z. B. Messprotokoll): Ein Foto liegt zuerst nur auf dem Gerät (IndexedDB, übersteht Funkloch und
 * Neuladen) – im Formular steht dann { id, name, lokal: true, ordner }. Beim Speichern des Formulars wird es in die
 * Dateien des Auftrags hochgeladen (Ordner «Fotos», Idempotency-Key = Foto-ID → nie doppelt) und im Formular durch
 * { name, dateiId } ersetzt. Ohne Netz wartet es mit dem vorgemerkten Formular.
 */

const DB = 'bs-fotos';
const STORE = 'fotos';
const speicher = new Map(); // Ersatz, wenn IndexedDB fehlt (Tests, privater Modus)
let dbVersprechen = null;

function db() {
  if (typeof indexedDB === 'undefined') return Promise.resolve(null);
  dbVersprechen ??= new Promise((resolve) => {
    try {
      const req = indexedDB.open(DB, 1);
      req.onupgradeneeded = () => req.result.createObjectStore(STORE, { keyPath: 'id' });
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => resolve(null);
    } catch { resolve(null); }
  });
  return dbVersprechen;
}

/** `fn(store)` gibt eine IDB-Anfrage zurück; ohne IndexedDB bekommt sie `null` und liefert den Wert direkt. */
async function tx(modus, fn) {
  const d = await db();
  if (!d) return fn(null);
  return new Promise((resolve, reject) => {
    const t = d.transaction(STORE, modus);
    const req = fn(t.objectStore(STORE));
    t.oncomplete = () => resolve(req?.result);
    t.onerror = () => reject(t.error ?? new Error('Foto konnte auf dem Gerät nicht gespeichert werden'));
  });
}

/** Foto auf dem Gerät ablegen. @param {{ id: string, fileName: string, base64: string }} foto */
export async function fotoAblegen(foto) {
  const eintrag = { ...foto, am: Date.now() };
  await tx('readwrite', (s) => (s ? s.put(eintrag) : void speicher.set(foto.id, eintrag)));
}

/** @returns {Promise<{ id: string, fileName: string, base64: string, am: number } | null>} */
export async function fotoLaden(id) {
  const r = await tx('readonly', (s) => (s ? s.get(id) : speicher.get(id)));
  return r ?? null;
}

export async function fotoLoeschen(id) {
  await tx('readwrite', (s) => (s ? s.delete(id) : void speicher.delete(id)));
}

async function alleIds() {
  return (await tx('readonly', (s) => (s ? s.getAll() : [...speicher.values()]))) ?? [];
}

const istLokal = (x) => x && typeof x === 'object' && x.lokal === true && typeof x.id === 'string';

/** Alle Foto-Listen eines Formulars (oberste Ebene: Kopf- und Schlussfelder). */
const fotoListen = (daten) => Object.entries(daten ?? {}).filter(([, w]) => Array.isArray(w) && w.some(istLokal));

/** Noch nicht hochgeladene Fotos im Formular. */
export const lokaleFotos = (daten) => fotoListen(daten).flatMap(([, w]) => w.filter(istLokal));

/** Ersetzt lokale Fotos (nach dem Hochladen) durch { name, dateiId }; `null` entfernt das Foto. */
export function fotosErsetzen(daten, ersetzt) {
  if (!daten || !ersetzt.size) return daten;
  const out = { ...daten };
  for (const [k, w] of fotoListen(daten)) {
    out[k] = w.flatMap((x) => (istLokal(x) && ersetzt.has(x.id) ? (ersetzt.get(x.id) ? [ersetzt.get(x.id)] : []) : [x]));
  }
  return out;
}

/**
 * Lokale Fotos eines Formulars in den Auftrag hochladen. Wirft bei Netzproblemen (das Formular wartet dann mit).
 * Fehlt ein Foto auf dem Gerät (Speicher gelöscht), fällt es weg.
 * @returns {Promise<{ daten: object, ersetzt: Map<string, { name: string, dateiId: string } | null> }>}
 */
export async function fotosHochladen(nr, daten) {
  const ersetzt = new Map();
  for (const f of lokaleFotos(daten)) {
    const foto = await fotoLaden(f.id);
    if (!foto) { ersetzt.set(f.id, null); continue; }
    const r = await api(`/auftraege/${encodeURIComponent(nr)}/dateien`, {
      method: 'POST',
      body: { fileName: f.name || foto.fileName, base64: foto.base64, ordnerName: f.ordner || 'Fotos' },
      idempotencyKey: `foto-${f.id}`,
      timeoutMs: 60_000,
    });
    ersetzt.set(f.id, { name: r.name ?? f.name, dateiId: String(r.id) });
  }
  return { daten: fotosErsetzen(daten, ersetzt), ersetzt };
}

/** Nach dem Speichern: hochgeladene Fotos vom Gerät entfernen. */
export async function fotosFreigeben(ersetzt) {
  for (const id of ersetzt.keys()) await fotoLoeschen(id).catch(() => {});
}

/** Fotos auf dem Gerät, die in keinem Entwurf mehr vorkommen (verworfen), nach einem Tag entfernen. */
export async function fotosAufraeumen() {
  try {
    const benutzt = new Set();
    for (let i = 0; i < window.localStorage.length; i++) {
      const raw = window.localStorage.getItem(window.localStorage.key(i)) ?? '';
      if (!raw.includes('"lokal":true')) continue;
      for (const m of raw.matchAll(/"id":"([^"]+)"/g)) benutzt.add(m[1]);
    }
    const grenze = Date.now() - 24 * 3600_000;
    for (const f of await alleIds()) if (!benutzt.has(f.id) && f.am < grenze) await fotoLoeschen(f.id);
  } catch { /* Speicher gesperrt */ }
}
